// MatchSimulator — pure padel match simulation shared by tournaments
// and leagues. No store access; fully deterministic with a seeded rng.

import { Rng, defaultRng } from '../utils/rng'

export interface PadelTeam {
  name: string // "Garcia / Lopez"
  rating: number // average level of the pair (1-100)
  form: number // average form of the pair (0-100)
  isClubTeam: boolean
}

export interface MatchResult {
  winner: 0 | 1
  sets: Array<[number, number]> // e.g. [[6,3],[4,6],[7,5]]
}

const HOME_ADVANTAGE = 4
const RATING_WEIGHT = 0.7
const FORM_WEIGHT = 0.3
// Logistic divisor: +10 effective rating ~ 71% per set, +25 ~ 91%
const LOGISTIC_DIVISOR = 25

export function getTeamEffectiveRating(team: PadelTeam, isHome = false): number {
  return team.rating * RATING_WEIGHT + team.form * FORM_WEIGHT + (isHome ? HOME_ADVANTAGE : 0)
}

/** Probability that team A wins a single set against team B */
export function setWinProbability(effA: number, effB: number): number {
  return 1 / (1 + Math.pow(10, (effB - effA) / LOGISTIC_DIVISOR))
}

/** Loser's games in a set: closer matches produce tighter scores */
function loserGames(pWinner: number, rng: Rng): number {
  if (pWinner > 0.85) return Math.floor(rng() * 3) // 0-2: one-sided
  if (pWinner > 0.65) return Math.floor(rng() * 4) + 1 // 1-4
  return Math.floor(rng() * 2) + 4 // 4-5: tight set
}

export interface SimulateOptions {
  homeAdvantage?: 0 | 1 // index of the home team, if any
  rng?: Rng
}

/** Simulate a best-of-3 padel match */
export function simulateMatch(a: PadelTeam, b: PadelTeam, opts: SimulateOptions = {}): MatchResult {
  const rng = opts.rng ?? defaultRng
  const effA = getTeamEffectiveRating(a, opts.homeAdvantage === 0)
  const effB = getTeamEffectiveRating(b, opts.homeAdvantage === 1)
  const pA = setWinProbability(effA, effB)

  const sets: Array<[number, number]> = []
  let winsA = 0
  let winsB = 0

  while (winsA < 2 && winsB < 2) {
    const aTakesSet = rng() < pA
    const pSetWinner = aTakesSet ? pA : 1 - pA
    let loserScore = loserGames(pSetWinner, rng)
    let winnerScore = 6
    if (loserScore === 5) {
      // tight set: 7-5 or tie-break 7-6
      winnerScore = 7
      if (rng() < 0.5) loserScore = 6
    }

    if (aTakesSet) {
      winsA++
      sets.push([winnerScore, loserScore])
    } else {
      winsB++
      sets.push([loserScore, winnerScore])
    }
  }

  return { winner: winsA === 2 ? 0 : 1, sets }
}

/** Format a result for display: "6-3, 4-6, 7-5" */
export function formatSets(result: MatchResult): string {
  return result.sets.map(([a, b]) => `${a}-${b}`).join(', ')
}
